import { LivestreamRequester } from '@cobliteam/api-lib';

import type { LivestreamResponse } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { type QueryKey, queryOptions, type UseMutationOptions, type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();
const QUERY_KEY: QueryKey = ['CAMERA-LIVESTREAM'];

const livestreamRequester = new LivestreamRequester({
  host: env.apiHost,
});

const getDeviceLivestreamOptions = (deviceId: string, options?: Partial<UseQueryOptions<LivestreamResponse>>) => {
  return queryOptions<LivestreamResponse>({
    queryKey: [...QUERY_KEY, deviceId],
    queryFn: () => livestreamRequester.getLivestream({ deviceId }),
    enabled: !!deviceId,
    ...options,
  });
};

const useCloseDeviceLivestream = (deviceId: string, options?: Partial<UseMutationOptions<void, Error, void>>) => {
  const queryClient = useQueryClient();

  return useMutation<void>({
    mutationKey: [...QUERY_KEY, deviceId, 'close'],
    mutationFn: () => livestreamRequester.closeLivestream({ deviceId }),
    onSuccess: () => {
      // Remove queries completely to ensure fresh data on reconnect
      queryClient.removeQueries({ queryKey: [...QUERY_KEY, deviceId] });
    },
    ...options,
  });
};

const useDeviceLivestream = () => {
  const getDeviceLivestream = (deviceId: string, options?: Partial<UseQueryOptions<LivestreamResponse>>) => {
    return useQuery<LivestreamResponse>({
      queryKey: [...QUERY_KEY, deviceId],
      queryFn: () => livestreamRequester.getLivestream({ deviceId }),
      ...options,
    });
  };

  return {
    getDeviceLivestream,
  };
};

export default useDeviceLivestream;

export { getDeviceLivestreamOptions, useCloseDeviceLivestream };
